/**
 * Mobile Splash Page Component
 * 
 * A simplified mobile version of the main portal with only essential features.
 * Provides easy access to the most important portal functions for mobile users.
 * 
 * Enhanced with prefetching capabilities to improve performance when users
 * navigate to news and events pages.
 */

import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import MobileLayout from './MobileLayout';
import { prefetchAll } from '../../utils/prefetchUtils';
import './MobileSplash.css';

/**
 * Mobile Splash Component
 * 
 * Provides a simplified mobile homepage with essential navigation options.
 * Automatically prefetches news and events data for improved performance.
 * 
 * @returns {JSX.Element} Mobile splash page
 */
const MobileSplash = () => {
  // Prefetch all content when component mounts
  useEffect(() => {
    // Run prefetching in the background without blocking the UI
    prefetchAll(5, 5, 10); // Prefetch first 5 news, 5 events, and 10 media items
  }, []);

  const quickActions = [
    {
      path: '/mobile/news',
      title: 'Latest News',
      subtitle: 'Stay updated with industry news',
      icon: 'fas fa-newspaper',
      color: '#1976d2'
    },
    {
      path: '/mobile/events',
      title: 'Upcoming Events',
      subtitle: 'View and register for events',
      icon: 'fas fa-calendar-alt',
      color: '#388e3c'
    },
    {
      path: '/mobile/media',
      title: 'Media Centre',
      subtitle: 'Videos, podcasts, and resources',
      icon: 'fas fa-play-circle',
      color: '#f57c00'
    },
    {
      path: '/mobile/institutional-intelligence',
      title: 'Institutional Intelligence',
      subtitle: 'Official regulatory & policy updates',
      icon: 'intelligence-icon',
      color: '#00838f'
    },
    {
      path: '/mobile/daire',
      title: 'Ask Daire',
      subtitle: 'Your Irish Funds AI Assistant',
      icon: 'daire-icon',
      color: '#e91e63'
    },
    {
      path: '/mobile/etf',
      title: 'ETF Screener',
      subtitle: 'Powered by ETFBook',
      icon: 'fas fa-book',
      color: '#7b1fa2'
    }
  ];

  const memberTools = [
    {
      path: '/mobile/profile',
      title: 'My Profile',
      icon: 'fas fa-user',
      description: 'Update your profile and preferences'
    },
    {
      path: '/mobile/daire',
      title: 'Daire AI',
      icon: 'daire-icon',
      description: 'Get info from our AI assistant'
    }
  ];

  return (
    <MobileLayout title="Irish Funds">
      <div className="mobile-splash">
        {/* Welcome Section */}
        <div className="mobile-welcome">
          <div className="mobile-welcome-content">
            <h1 className="mobile-welcome-title">Member Portal</h1>
            <p className="mobile-welcome-subtitle">
             The mobile version
            </p>
          </div>
        </div>

        {/* Quick Actions */}
        <section className="mobile-section">
          {/* <h2 className="mobile-section-title">Available Features</h2> */}
          <div className="mobile-quick-actions">
            {quickActions.map((action, index) => (
              <Link
                key={index}
                to={action.path}
                className="mobile-quick-action"
                style={{ borderLeftColor: action.color }}
              >
                <div className="mobile-quick-action-icon" style={{ color: action.color }}>
                  {action.icon === 'daire-icon' ? (
                    <img src="/assets/img/daire/daire-logo-icon.png" alt="Daire AI" className="daire-icon" />
                  ) : action.icon === 'intelligence-icon' ? (
                    <img
                      src="/images/intelligence-centre-icon-256.png"
                      alt="Institutional Intelligence"
                      style={{ width: '36px', height: '36px', objectFit: 'contain' }}
                    />
                  ) : (
                    <i className={action.icon}></i>
                  )}
                </div>
                <div className="mobile-quick-action-content">
                  <h3 className="mobile-quick-action-title">{action.title}</h3>
                  <p className="mobile-quick-action-subtitle">{action.subtitle}</p>
                </div>
                <div className="mobile-quick-action-arrow">
                  <i className="fas fa-chevron-right"></i>
                </div>
              </Link>
            ))}
          </div>
        </section>

        {/* Member Tools */}
        <section className="mobile-section" style={{ display: 'none' }}>
          <h2 className="mobile-section-title">Member Tools</h2>
          <div className="mobile-member-tools">
            {memberTools.map((tool, index) => (
              <Link
                key={index}
                to={tool.path}
                className="mobile-member-tool"
              >
                <div className="mobile-member-tool-icon">
                  {tool.icon === 'daire-icon' ? (
                    <img src="/assets/img/daire/daire-logo-icon.png" alt="Daire AI" className="daire-icon" />
                  ) : (
                    <i className={tool.icon}></i>
                  )}
                </div>
                <div className="mobile-member-tool-content">
                  <h3 className="mobile-member-tool-title">{tool.title}</h3>
                  <p className="mobile-member-tool-description">{tool.description}</p>
                </div>
              </Link>
            ))}
          </div>
        </section>

        {/* Additional Options */}
        <section className="mobile-section" style={{ display: 'none' }}>
          <h2 className="mobile-section-title">More Options</h2>
          <div className="mobile-additional-options">
            <Link to="/mobile/policy" className="mobile-additional-option">
              <i className="fas fa-gavel"></i>
              <span>Policy & Regulatory</span>
            </Link>
            <Link to="/mobile/groups" className="mobile-additional-option">
              <i className="fas fa-users"></i>
              <span>Working Groups</span>
            </Link>
            <Link to="/mobile/innovation" className="mobile-additional-option">
              <i className="fas fa-lightbulb"></i>
              <span>Innovation</span>
            </Link>
            <Link to="/mobile/council" className="mobile-additional-option">
              <i className="fas fa-building"></i>
              <span>Council Corner</span>
            </Link>
          </div>
        </section>

      </div>
    </MobileLayout>
  );
};

export default MobileSplash; 